import type { CSSProperties, ReactNode } from 'react';
import type { CaseView, LiveState } from '@chemoflow/contracts';
import { stateOf } from '@chemoflow/domain';
import { fmtBigParts, minutesSince } from '../lib/format.js';
import { useBoardData } from '../state/board.js';
import { Icon, IconSwap } from './Icon.js';

/** 大きく見せる時刻。数字を主役に、「時・分」はひと回り小さく。 */
/** 条件付きのクラス名を、余計な空白を作らずに並べる。 */
export const cls = (...parts: (string | false | null | undefined)[]): string =>
  parts.filter(Boolean).join(' ');

export function BigTime({ ts }: { ts: number }) {
  const { h, m } = fmtBigParts(ts);
  return (
    <>
      {h}
      <i className="u">時</i>
      {m}
      <i className="u">分</i>
    </>
  );
}

export const Since = ({ ts, now }: { ts: number; now: number }) => <>{minutesSince(ts, now)}</>;

export interface LiveStatus {
  cls: LiveState;
  node: ReactNode;
}

const STATUS_ICON: Record<Exclude<LiveState, 'idle' | 'admin'>, string> = {
  confirm: 'check',
  change: 'autorenew',
  stop: 'close',
  start: 'play_arrow',
  made: 'done_all',
  done: 'task_alt',
};

const STATUS_LABEL: Record<Exclude<LiveState, 'idle' | 'admin'>, string> = {
  confirm: '調製待ち',
  change: '調製待ち',
  stop: '中止',
  start: '調製開始済み',
  made: '調製完了',
  done: '投与完了',
};

/**
 * 名前の前のタグ。全画面で常に「いまの状態」を出す。
 * 入力前 → 調製待ち → 調製開始済み → 調製完了 → 投与から◯分 → 投与完了（中止は割り込み）。
 */
export function liveStatus(c: CaseView, now: number): LiveStatus {
  const st = stateOf(c);
  if (st === 'idle') return { cls: 'idle', node: '入力前' };
  if (st === 'admin') {
    return {
      cls: 'admin',
      node: (
        <>
          <Icon name="water_drop" />
          投与から<span>{minutesSince(c.admin?.at ?? now, now)}</span>分
        </>
      ),
    };
  }
  return {
    cls: st,
    node: (
      <>
        <Icon name={STATUS_ICON[st]} />
        {STATUS_LABEL[st]}
      </>
    ),
  };
}

export function StatusPill({ case: c, now }: { case: CaseView; now: number }) {
  const s = liveStatus(c, now);
  return <div className={`status ${s.cls}`}>{s.node}</div>;
}

export interface TrailStep {
  label: string;
  ts: number | null | undefined;
  /** 点の色 */
  color: string;
  /** 「いまここ」の段の時刻の色（省略時は点と同じ） */
  textColor?: string;
}

/** 最初の段は外来の入力：確定／変更／中止で見出しと色を変える。 */
const CLERK_STEP = {
  confirm: { label: '確定', color: 'var(--primary)', textColor: 'var(--primary)' },
  change: { label: '変更', color: 'var(--change)', textColor: 'var(--change-text)' },
  stop: { label: '中止', color: 'var(--stop)', textColor: 'var(--stop)' },
} as const;

/** タイムラインの各段。確定済みHTMLの STEPS と同じ定義。 */
export const TRAIL_STEP = {
  clerk: (c: CaseView): TrailStep => ({
    ...CLERK_STEP[c.clerk?.type ?? 'confirm'],
    ts: c.clerk?.at,
  }),
  pharm: (c: CaseView): TrailStep => ({
    label: '調製開始',
    ts: c.pharm?.at,
    color: 'var(--start)',
    textColor: 'var(--start-text)',
  }),
  pharmDone: (c: CaseView): TrailStep => ({
    label: '調製完了',
    ts: c.pharmDone?.at,
    color: 'var(--start)',
    textColor: 'var(--made-text)',
  }),
  admin: (c: CaseView): TrailStep => ({
    label: '投与開始',
    ts: c.admin?.at,
    color: 'var(--admin)',
    textColor: 'var(--admin-text)',
  }),
  adminDone: (c: CaseView): TrailStep => ({
    label: '投与完了',
    ts: c.adminDone?.at,
    color: 'var(--admin)',
    textColor: 'var(--done-text)',
  }),
};

/**
 * 時刻のタイムライン（全体ボード・薬剤待ちで共通）。見出し／点／時刻の3段。
 * 済んだ段は部署の色で点を塗り、いちばん新しい段だけ時刻を色付きにして「いまここ」を示す。
 */
export function Trail({ steps, lg = false }: { steps: readonly TrailStep[]; lg?: boolean }) {
  const last = steps.map((s) => Boolean(s.ts)).lastIndexOf(true);
  return (
    <div className={cls('trail', lg && 'is-lg')}>
      {steps.map((s, i) => (
        <div
          key={i}
          className={cls('when', !s.ts && 'is-none', i === last && 'is-now')}
          style={{ '--c': s.color, '--ct': s.textColor ?? s.color } as CSSProperties}
        >
          <small>{s.label}</small>
          <i />
          <span>{s.ts ? <BigTime ts={s.ts} /> : '―'}</span>
        </div>
      ))}
    </div>
  );
}

export interface DoneButtonProps {
  label: string;
  icon: string;
  action: string;
  /** 化学療法室の色（投与完了）にする */
  admin?: boolean;
  disabled?: boolean;
  /** 押せない理由（ホバーで出す） */
  disabledReason?: string;
  onClick(): void;
}

/**
 * 完了ボタン（調製完了／投与完了）。押した先の「完了」ピルと同じ淡色で、
 * ホバーで塗りつぶし、アイコンが「↓」（下の完了欄へ移る）に入れ替わる。
 */
export function DoneButton({
  label,
  icon,
  action,
  admin = false,
  disabled,
  disabledReason,
  onClick,
}: DoneButtonProps) {
  return (
    <button
      className={cls('btn-done act', admin && 'is-admin')}
      type="button"
      data-action={action}
      onClick={onClick}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
    >
      <IconSwap name={icon} to="arrow_downward" />
      {label}
    </button>
  );
}

export interface WhoProps {
  case: CaseView;
  rp?: boolean;
  onUndo?: () => void;
}

/**
 * 患者の識別欄。患者ID・診療科を併記して取り違えを防ぐ（要件 #14）。
 * 氏名が取れないとき（縮退）は、CSSが .name-txt を隠して .name-hidden を出す。
 */
export function PatientWho({ case: c, rp = false, onUndo }: WhoProps) {
  const { hasSameSurname, isBusy } = useBoardData();
  const dup = hasSameSurname(c);
  const p = c.patient;
  return (
    <div className="who">
      <div className="name">
        <span className="name-txt">{p?.name ?? ''}</span>
        <span className="name-hidden">氏名を表示できません</span>
        {dup ? (
          <span className="dup" title="同じ姓の患者がいます。IDと生年月日で確認してください">
            <Icon name="warning" />
            姓が同じ患者あり
          </span>
        ) : null}
        {onUndo ? (
          // 送信中は押せなくする。2回目のタップはサーバに版で弾かれ、
          // 自分の操作なのに「他の端末で」と出てしまう（見た目は確定済みHTMLのまま）
          <button className="undo act" type="button" onClick={onUndo} disabled={isBusy(c.caseId)}>
            ← 戻す
          </button>
        ) : null}
      </div>
      <div className="meta">
        <span className="id">ID：{p?.patientId ?? c.surrogateKey}</span>
        {dup && p?.birth ? <span className="birth">{p.birth}生</span> : null}
        <span>{p?.dept ?? ''}</span>
        <span>{p?.age != null ? `${p.age}歳` : ''}</span>
        <span>{p?.sex ?? ''}</span>
        {rp ? <span className="rp">Rp {c.rpCount}</span> : null}
      </div>
    </div>
  );
}

export interface DoneArticleProps {
  case: CaseView;
  tone: string;
  pillCls?: string;
  pill: ReactNode;
  label: string;
  ts: number;
  middle?: ReactNode;
  rp?: boolean;
  extra?: string;
  onUndo?: () => void;
  /** 時刻の右に置く操作（調製完了・投与完了）。 */
  action?: ReactNode;
}

/** 済みカード。左にピル、中央に患者と差し込み、右に時刻。 */
export function DoneArticle({
  case: c,
  tone,
  pillCls,
  pill,
  label,
  ts,
  middle,
  rp = false,
  extra = '',
  onUndo,
  action,
}: DoneArticleProps) {
  return (
    <article
      className={cls('card is-done', `tone-${tone}`, action ? 'has-action' : null, extra)}
      data-id={c.caseId}
    >
      <div className="row">
        <div className={`status ${pillCls ?? tone}`}>{pill}</div>
        <PatientWho case={c} rp={rp} onUndo={onUndo} />
        {middle}
        <div className="done-time">
          <small>{label}</small>
          <b>
            <BigTime ts={ts} />
          </b>
        </div>
        {action}
      </div>
    </article>
  );
}

export interface ListSectionProps {
  title: string;
  count: number;
  emptyText?: string;
  headClass?: string;
  hideWhenEmpty?: boolean;
  /** 見出しの前に置くアイコン（完了の欄） */
  icon?: string;
  /** 見出しの下に添える注記 */
  note?: string;
  children: ReactNode;
}

/** 列の見出しと件数、空表示。確定済み画面の refresh() が担っていた役割。 */
export function ListSection({
  title,
  count,
  emptyText,
  headClass = '',
  hideWhenEmpty = false,
  icon,
  note,
  children,
}: ListSectionProps) {
  if (hideWhenEmpty && count === 0) return null;
  return (
    <div className="group">
      <div className={`col-head ${headClass}`.trim()}>
        <span>
          {icon ? <Icon name={icon} /> : null}
          {title} <em className="count">{count}</em>
        </span>
      </div>
      {note ? <p className="col-note">{note}</p> : null}
      <div className="list">
        {count === 0 && emptyText ? <p className="empty">{emptyText}</p> : children}
      </div>
    </div>
  );
}
